/**
 * src/content/templates.ts — starter templates (Appendix E).
 *
 * Tax fields in every template INHERIT the user's tax profile if set, else
 * GENERIC_TAX_DEFAULTS — templates never embed specific tax rates (§6.20).
 */
import type { ScenarioInputs, TaxProfile } from '@/types';
import { defaultScenarioInputs } from '@/types';

export interface Template {
  name: string;
  description: string;
  build: (latestSofr: number, taxProfile: TaxProfile | null) => ScenarioInputs;
}

export const TEMPLATES: Template[] = [
  {
    name: 'Conservative income',
    description: '$1M portfolio, advance 0.65, $3,000/mo interest-only, 6% return, flat rate, 15y. Big cushion, modest draw. Tax rates are estimates — edit to match your situation.',
    build: (sofr, tax) => ({
      ...defaultScenarioInputs(sofr, tax),
      portfolioValue: 1_000_000,
      blendedAdvanceRate: 0.65,
      drawAmount: 3_000,
      paymentMode: 'interest_only',
      portfolioExpectedReturnAnnualPct: 6,
      years: 15,
    }),
  },
  {
    name: 'Buy-borrow-die illustration',
    description: '$3M portfolio, advance 0.70, $10,000/mo interest-only, 7% return, basis 30%, estate view on, 30y. Highlights tax avoided vs selling. Tax rates are estimates — edit to match your situation.',
    build: (sofr, tax) => {
      const base = defaultScenarioInputs(sofr, tax);
      return {
        ...base,
        portfolioValue: 3_000_000,
        blendedAdvanceRate: 0.7,
        drawAmount: 10_000,
        paymentMode: 'interest_only',
        portfolioExpectedReturnAnnualPct: 7,
        costBasisPct: 30,
        years: 30,
        tax: { ...base.tax, estateTaxEnabled: true },
      };
    },
  },
  {
    name: 'Rate-shock stress test',
    description: '$1M portfolio, $6,000/mo, interest compounds, stress spike +500 then revert, forced liquidation on, 10y. Shows call risk and forced sales. Tax rates are estimates — edit to match your situation.',
    build: (sofr, tax) => ({
      ...defaultScenarioInputs(sofr, tax),
      portfolioValue: 1_000_000,
      drawAmount: 6_000,
      paymentMode: 'capitalize',
      ratePath: { type: 'stress', baseRatePct: sofr, stressPreset: 'spike_500_revert' },
      modelForcedLiquidation: true,
      years: 10,
    }),
  },
  {
    name: 'Short-term bridge',
    description: '$500k portfolio, one-time $150,000 draw, fixed payment over ~24 months, flat rate, 3y. Shows a quick payoff. Tax rates are estimates — edit to match your situation.',
    build: (sofr, tax) => ({
      ...defaultScenarioInputs(sofr, tax),
      portfolioValue: 500_000,
      drawFrequency: 'one_time',
      drawAmount: 150_000,
      paymentMode: 'fixed',
      fixedPayment: 6_700,
      years: 3,
    }),
  },
];
